import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CMultiSelect,
} from '@coreui/react-pro'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import FixtureService from 'src/service/FixtureService'
import RoundService from 'src/service/RoundService'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  findRoundWindow,
  isWithinRoundWindow,
  roundMaxDate,
  roundMinDate,
  roundOptionLabel,
  roundRangeMessage,
} from 'src/utils/roundWindow'

const AddForm = ({
  visible,
  onClose,
  gameType,
  activePage,
  columnFilter,
  columnSorter,
  itemsPerPage,
  fetchData,
}) => {
  const [loader, setLoader] = useState(false)
  const [matchTypeList, setMatchTypeList] = useState()
  const [errorMessages, setErrorMessages] = useState([])
  const [multiOption, setMultiOption] = useState([])
  const [roundList, setRoundList] = useState([])
  const [selectedRoundId, setSelectedRoundId] = useState('')
  const keepOpenRef = useRef(false)

  const roundWindow = useMemo(
    () => findRoundWindow(roundList, selectedRoundId),
    [roundList, selectedRoundId],
  )
  const outOfRoundMessage = roundRangeMessage(roundWindow)

  const validationSchema = Yup.object().shape({
    team: Yup.array()
      .of(Yup.string())
      .min(1, 'Please select at least one team')
      .required('Please select team'),
    match_type: Yup.string().required('Match type is required'),
    start_date: Yup.string()
      .required('Start date is required')
      .test('start-date-in-round', outOfRoundMessage, (value) =>
        isWithinRoundWindow(roundWindow, value, null),
      ),
    end_date: Yup.string()
      .required('End date is required')
      .test('end-date-in-round', outOfRoundMessage, (value) =>
        isWithinRoundWindow(roundWindow, value, null),
      ),
    start_time: Yup.string()
      .required('Start time is required')
      .test('start-time-in-round', outOfRoundMessage, function (value) {
        return isWithinRoundWindow(roundWindow, this.parent.start_date, value)
      }),
    end_time: Yup.string()
      .required('End time is required')
      .test('end-time-in-round', outOfRoundMessage, function (value) {
        return isWithinRoundWindow(roundWindow, this.parent.end_date, value)
      }),
  })

  useEffect(() => {
    if (visible) {
      TeamService.getTeamListByGrade().then((res) => {
        if (res.status === 200) {
          setMultiOption(res.teamArray)
          setMatchTypeList(res.matchTypeList)
        }
      })
      RoundService.getRoundDropDown().then((res) => {
        if (res.status === 200) {
          setRoundList(res.data || [])
        }
      })
    }
  }, [visible])

  const formik = useFormik({
    initialValues: {
      team: [],
      round_id: '',
      match_type: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      opposition_club: '',
      vanue: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      setLoader(true)
      FixtureService.saveFixture(data)
        .then((res) => {
          if (res.success) {
            setErrorMessages([])
            fetchData(activePage, columnFilter, columnSorter, itemsPerPage)
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const resetFields = (actions) => {
    actions.resetForm()
    setSelectedRoundId('')
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  const resetDateFields = () => {
    formik.setValues({
      ...formik.values,
      round_id: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
    })
  }

  // Picking a round prefills the whole window; every field stays editable afterwards, just
  // pinned to that round's dates.
  const handleRoundChange = (event) => {
    const roundId = event.target.value
    setSelectedRoundId(roundId)

    const range = findRoundWindow(roundList, roundId)
    if (!range) {
      resetDateFields()
      return
    }

    const roundStartTime = range.start.format('HH:mm')
    const roundEndTime = range.end.format('HH:mm')
    formik.setValues({
      ...formik.values,
      round_id: roundId,
      start_date: range.start.format('YYYY-MM-DD'),
      end_date: range.end.format('YYYY-MM-DD'),
      start_time: roundStartTime,
      end_time: roundEndTime,
    })
  }

  // The native inputs already hand back "YYYY-MM-DD" / "HH:mm", the shape formik and the API use.
  const handleDateTimeChange = (field) => (event) => {
    formik.setTouched({ ...formik.touched, [field]: true })
    formik.setFieldValue(field, event.target.value)
  }

  const handleStartDate = (event) => {
    const value = event.target.value
    handleDateTimeChange('start_date')(event)

    // Keep an end date that still sits on or after the new start; only drop it when it doesn't.
    const endDate = formik.values.end_date
    if (endDate && value && moment(endDate).isBefore(moment(value), 'day')) {
      formik.setFieldValue('end_date', '')
    }
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 640 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Fixture</COffcanvasTitle>
          <div className="text-medium-emphasis small">Schedule a new fixture.</div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="round_id">
              Round
            </CFormLabel>
            <CFormSelect
              aria-label="Select Round"
              name="round_id"
              id="round_id"
              value={selectedRoundId}
              onChange={handleRoundChange}
            >
              <option value="">Select Round</option>
              {roundList.map((item) => (
                <option value={item.id} key={item.id}>
                  {roundOptionLabel(item)}
                </option>
              ))}
            </CFormSelect>
            <div className="text-medium-emphasis small mt-1">
              {roundWindow
                ? `Dates prefilled from Round ${
                    roundWindow.round
                  }. You can change them within ${roundWindow.start.format(
                    'DD/MM/YYYY h:mm A',
                  )} - ${roundWindow.end.format('DD/MM/YYYY h:mm A')}.`
                : 'Optional - picking a round prefills the fixture dates and times.'}
            </div>
          </CCol>

          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="team">
              Teams *
            </CFormLabel>
            <CMultiSelect
              options={multiOption}
              selectionType="tags"
              name="team"
              className={formik.errors.team && formik.touched.team ? 'is-invalid' : ''}
              onChange={(selectedOptions) => {
                formik.setFieldValue(
                  'team',
                  selectedOptions.map((option) => option.value),
                )
              }}
              onBlur={() => formik.setFieldTouched('team', true)}
              value={multiOption.filter((obj) => formik.values.team.includes(obj.value))}
            />
            {formik.errors.team && formik.touched.team && (
              <CFormFeedback invalid>{formik.errors.team}</CFormFeedback>
            )}
          </CCol>

          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="match_type">
              Match Type *
            </CFormLabel>
            <CFormSelect
              aria-label="Select Match Type"
              name="match_type"
              className={
                'form-control' +
                (formik.errors.match_type && formik.touched.match_type ? ' is-invalid' : '')
              }
              value={formik.values.match_type}
              onChange={formik.handleChange}
              id="match_type"
            >
              <option value={0}>Select Match Type</option>
              {matchTypeList &&
                matchTypeList.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.name}
                  </option>
                ))}
            </CFormSelect>
            {formik.errors.match_type && formik.touched.match_type && (
              <CFormFeedback invalid>{formik.errors.match_type}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="start_date">
              Start Date *
            </CFormLabel>
            <CFormInput
              type="date"
              id="start_date"
              name="start_date"
              value={formik.values.start_date}
              min={roundMinDate(roundWindow)}
              max={roundMaxDate(roundWindow)}
              invalid={!!(formik.errors.start_date && formik.touched.start_date)}
              onChange={handleStartDate}
            />
            {formik.errors.start_date && formik.touched.start_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="end_date">
              End Date *
            </CFormLabel>
            <CFormInput
              type="date"
              id="end_date"
              name="end_date"
              value={formik.values.end_date}
              min={formik.values.start_date || roundMinDate(roundWindow)}
              max={roundMaxDate(roundWindow)}
              invalid={!!(formik.errors.end_date && formik.touched.end_date)}
              onChange={handleDateTimeChange('end_date')}
            />
            {formik.errors.end_date && formik.touched.end_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="start_time">
              Start Time *
            </CFormLabel>
            <CFormInput
              type="time"
              id="start_time"
              name="start_time"
              value={formik.values.start_time}
              invalid={!!(formik.errors.start_time && formik.touched.start_time)}
              onChange={handleDateTimeChange('start_time')}
            />
            {formik.errors.start_time && formik.touched.start_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="end_time">
              End Time *
            </CFormLabel>
            <CFormInput
              type="time"
              id="end_time"
              name="end_time"
              value={formik.values.end_time}
              invalid={!!(formik.errors.end_time && formik.touched.end_time)}
              onChange={handleDateTimeChange('end_time')}
            />
            {formik.errors.end_time && formik.touched.end_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel className="fw-bold" htmlFor="vanue">
              Venue
            </CFormLabel>
            <input
              type="text"
              name="vanue"
              className={
                'form-control' + (formik.errors.vanue && formik.touched.vanue ? ' is-invalid' : '')
              }
              id="vanue"
              placeholder="Venue"
              value={formik.values.vanue}
              onChange={formik.handleChange}
            />
            {formik.errors.vanue && formik.touched.vanue && (
              <CFormFeedback invalid>{formik.errors.vanue}</CFormFeedback>
            )}
          </CCol>
          {gameType === 1 && (
            <CCol xs={6}>
              <CFormLabel className="fw-bold" htmlFor="opposition_club">
                Opponent
              </CFormLabel>
              <input
                type="text"
                name="opposition_club"
                className={
                  'form-control' +
                  (formik.errors.opposition_club && formik.touched.opposition_club
                    ? ' is-invalid'
                    : '')
                }
                id="opposition_club"
                placeholder="Opponent"
                value={formik.values.opposition_club}
                onChange={formik.handleChange}
              />
              {formik.errors.opposition_club && formik.touched.opposition_club && (
                <CFormFeedback invalid>{formik.errors.opposition_club}</CFormFeedback>
              )}
            </CCol>
          )}
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Fixture
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
